import { SxProps, Theme } from "@mui/material";
import supportTicketsTokens, {
  supportTicketsResponsiveTokens,
} from "./SupportTickets.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

const t = supportTicketsTokens;
const r = supportTicketsResponsiveTokens;

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
};

/** White rounded card wrapping the tab strip and the whole tickets list. */
export const ticketsCardSx: SxProps<Theme> = {
  /*
    Both small references drop the wrapper entirely: the segmented control and
    every ticket sit directly on the page canvas as their own blocks.
  */
  bgcolor: { xs: "transparent", md: t.color.surface },
  border: { xs: "none", md: `${t.border.hairline} solid ${t.color.cardBorder}` },
  borderRadius: { xs: 0, md: t.radius.card },
  boxShadow: { xs: "none", md: t.shadow.card },
  overflow: { xs: "visible", md: "hidden" },
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};

/** Strip carrying the Open / In Process / Complete tabs. */
export const tabBarSx: SxProps<Theme> = {
  px: { xs: 0, md: t.space.gutter },
  pt: { xs: 0, md: t.space.tabBarPaddingTop },
  borderBottom: {
    xs: "none",
    md: `${t.border.hairline} solid ${t.color.tabBarBorder}`,
  },
};

/**
 * The tab row is pulled down one pixel so each tab's own 2px underline sits on
 * top of the strip's hairline rather than stacking below it — exactly how the
 * design layers them.
 */
export const tabsSx: SxProps<Theme> = {
  minHeight: 0,
  mb: { xs: 0, md: `-${t.border.hairline}` },
  // Below `md` the strip becomes the reference's segmented control: a tinted
  // track holding three equal pills, the active one lifted on white.
  bgcolor: { xs: r.segmented.trackBg, md: "transparent" },
  borderRadius: { xs: r.segmented.trackRadius, md: 0 },
  p: { xs: r.segmented.trackPadding, md: 0 },
  "& .MuiTabs-flexContainer": {
    gap: { xs: r.segmented.gap, md: t.space.tabGap },
  },
  "& .MuiTabs-indicator": { display: "none" },
  // `variant="scrollable"` would otherwise let the three pills keep their
  // natural widths and scroll; the reference splits the track evenly.
  "& .MuiTabs-scroller": { overflow: { xs: "visible !important", md: "auto" } },
};

export const tabSx: SxProps<Theme> = {
  minWidth: 0,
  minHeight: { xs: r.segmented.itemHeight, md: 0 },
  height: { xs: r.segmented.itemHeight, md: "auto" },
  flex: { xs: 1, md: "none" },
  justifyContent: "center",
  borderRadius: { xs: r.segmented.itemRadius, md: 0 },
  // MUI stacks the tab as a column flex box and centres it on the cross (i.e.
  // horizontal) axis; the design lays the label out in normal flow behind its
  // 14px padding. Centring rounds to a different half-pixel, so pin it left.
  alignItems: { xs: "center", md: "flex-start" },
  px: { xs: 0, md: t.space.tabPaddingX },
  py: { xs: 0, md: t.space.tabPaddingY },
  fontSize: { xs: r.segmented.fontSize, md: t.font.tab },
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  textTransform: "none",
  color: { xs: r.segmented.idleColor, md: t.color.muted },
  borderBottom: {
    xs: "none",
    md: `${t.border.tabIndicator} solid transparent`,
  },
  // The theme spaces adjacent tabs by 32px; the design spaces them with the
  // flex container's 4px gap alone.
  "&.MuiTab-root + &.MuiTab-root": { ml: 0 },
  "&.Mui-disabled": { color: t.color.muted, opacity: 1 },
  "&.Mui-selected": {
    color: { xs: r.segmented.activeColor, md: t.color.accent },
    bgcolor: { xs: r.segmented.activeBg, md: "transparent" },
    boxShadow: { xs: r.segmented.activeShadow, md: "none" },
    borderBottomColor: { md: t.color.accent },
  },
};

/** Row count trailing a tab label; hidden when the tab is empty. */
export const tabLabelSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: t.space.tabCountGap,
};

export const tabCountSx: SxProps<Theme> = {
  fontSize: t.font.tabCount,
  fontWeight: t.weight.strong,
  color: "inherit",
  lineHeight: "normal",
};

/** Grey "# / SUMMARY / CATEGORY / …" strip. */
export const columnHeaderSx: SxProps<Theme> = {
  // The six fixed tracks cannot fit a phone or an 834 tablet; below `md` the
  // rows become cards and the strip goes with them.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: t.grid.row,
  px: t.space.gutter,
  py: t.space.columnHeaderY,
  bgcolor: t.color.headerBg,
  borderBottom: `${t.border.hairline} solid ${t.color.headerBorder}`,
  fontSize: t.font.columnHeader,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.columnHeader,
  color: t.color.subtle,
  lineHeight: "normal",
};

/** One ticket row. The last row drops its divider so the card closes cleanly. */
export const rowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: t.grid.row,
  alignItems: "center",
  px: t.space.gutter,
  py: t.space.rowY,
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
});

export const ticketNumberSx: SxProps<Theme> = {
  fontSize: t.font.cell,
  fontWeight: t.weight.medium,
  color: t.color.subtle,
  lineHeight: "normal",
};

/**
 * Ticket summary. Rendered as a router link but stripped back to the design's
 * plain bold ink, picking up the accent only on hover.
 */
export const summaryLinkSx: SxProps<Theme> = {
  display: "block",
  minWidth: 0,
  pr: t.space.priorityGap,
  fontSize: t.font.summary,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
  textDecoration: "none",
  wordBreak: "break-word",
  "&:hover": { color: t.color.accent },
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "-2px",
    borderRadius: "3px",
  },
};

/** Category and assignee cells. */
const cellBase = {
  minWidth: 0,
  pr: t.space.priorityGap,
  fontSize: t.font.cell,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const cellSx: SxProps<Theme> = cellBase;

/**
 * Assignee cell whose list is collapsed behind a tooltip. It takes a tab stop
 * so the tooltip can be opened with the keyboard, which needs a visible focus
 * ring (WCAG 2.4.7); geometry is untouched.
 */
export const truncatedCellSx: SxProps<Theme> = {
  ...cellBase,
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "-2px",
    borderRadius: "3px",
  },
};

const priorityColors: Record<string, string> = {
  High: t.color.priorityHigh,
  Medium: t.color.priorityMedium,
  Low: t.color.priorityLow,
};

/** Priority label preceded by its colour dot. */
export const prioritySx = (priority: string): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  // Grid items blockify `inline-flex` and stretch to the full column, so the
  // box has to be pinned back to its content width.
  justifySelf: "start",
  gap: t.space.priorityGap,
  fontSize: t.font.cell,
  fontWeight: t.weight.medium,
  color: priorityColors[priority] ?? t.color.muted,
  lineHeight: "normal",
  whiteSpace: "nowrap",
});

export const priorityDotSx = (priority: string): SxProps<Theme> => ({
  width: t.size.priorityDot,
  height: t.size.priorityDot,
  flexShrink: 0,
  borderRadius: t.radius.pill,
  bgcolor: priorityColors[priority] ?? t.color.muted,
});

/** Neutral "Unassigned" pill, built on the design's permission-pill geometry. */
export const unassignedPillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  // Without this the pill blockifies as a grid item and fills the 150px
  // assignee column instead of hugging its label.
  justifySelf: "start",
  maxWidth: "100%",
  px: t.space.pillPaddingX,
  py: t.space.pillPaddingY,
  borderRadius: t.radius.pill,
  bgcolor: t.color.pillBg,
  color: t.color.pillText,
  fontSize: t.font.pill,
  fontWeight: t.weight.medium,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/** "Raised by / on" stacks the requester above the timestamp. */
export const stackedCellSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.stackedCellGap,
  minWidth: 0,
};

export const stackedCellPrimarySx: SxProps<Theme> = {
  fontSize: t.font.cell,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const stackedCellSecondarySx: SxProps<Theme> = {
  fontSize: t.font.timestamp,
  fontWeight: t.weight.regular,
  color: t.color.subtle,
  lineHeight: "normal",
};

/** Centred placeholder shown in place of rows when the tab is empty. */
export const emptyStateSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: t.space.emptyGap,
  p: t.space.emptyPadding,
};

export const emptyIconWellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: t.size.emptyIconWell,
  height: t.size.emptyIconWell,
  borderRadius: t.radius.pill,
  bgcolor: t.color.emptyIconBg,
};

export const emptyIconSx: SxProps<Theme> = {
  width: t.size.emptyIcon,
  height: t.size.emptyIcon,
  fill: "none",
};

export const emptyTitleSx: SxProps<Theme> = {
  fontSize: t.font.emptyTitle,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

export const emptyTextSx: SxProps<Theme> = {
  fontSize: t.font.emptyText,
  color: t.color.subtle,
  lineHeight: "normal",
  textAlign: "center",
};

/** Footer strip hosting the pager. */
export const paginationBarSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  px: t.space.gutter,
  py: t.space.footerY,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

/** MUI <Pagination> restyled to the design's 28px square items. */
export const paginationSx: SxProps<Theme> = {
  "& .MuiPagination-ul": { gap: t.space.paginationGap, flexWrap: "nowrap" },
  "& .MuiPagination-ul > li": { m: 0 },
  "& .MuiPaginationItem-root": {
    minWidth: t.size.pageItem,
    width: t.size.pageItem,
    height: t.size.pageItem,
    m: 0,
    p: 0,
    borderRadius: t.radius.pageItem,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.medium,
    color: t.color.pageItem,
    "&:hover": { bgcolor: t.color.pageHoverFill },
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    bgcolor: t.color.ctaBg,
    color: t.color.onAccent,
    fontWeight: t.weight.strong,
    "&:hover": { bgcolor: t.color.ctaBg, color: t.color.onAccent },
  },
  "& .MuiPaginationItem-ellipsis": {
    width: "auto",
    minWidth: "unset",
    px: t.space.paginationGap,
    height: t.size.pageItem,
    display: "flex",
    alignItems: "center",
    color: t.color.subtle,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.regular,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    color: t.color.pageDisabled,
    opacity: 1,
  },
};

/** Filled "Create Ticket" action in the page header. */
export const createTicketButtonSx: SxProps<Theme> = {
  // Both small references run it full width under the search box.
  width: { xs: "100%", md: "auto" },
  height: { xs: r.action.height, md: t.size.buttonHeight },
  minHeight: { xs: r.action.height, md: t.size.buttonHeight },
  px: { xs: r.action.buttonPaddingX, md: t.size.buttonPaddingX },
  py: 0,
  color: t.color.onAccent,
  border: "none",
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  // `MuiButton.contained` paints with the `background` *shorthand*, which
  // emotion emits after this object's `background-color` longhand and so
  // resets it back to the palette colour. Pin the fill one specificity step
  // higher so source order stops mattering.
  "&.MuiButton-root": {
    background: t.color.ctaBg,
    "&:hover": { background: t.color.ctaBgHover, boxShadow: "none" },
    "&.Mui-disabled": {
      background: t.color.ctaBg,
      opacity: 0.5,
      color: t.color.onAccent,
    },
  },
};

/* ---------------------------------------------------------------- card list */

/**
 * Below `md` each ticket is its own card — `#id` + priority chip + category,
 * the summary, then a ruled footer with the reporter and the raised-on date.
 */
export const ticketCardSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: r.card.gap,
  p: r.card.padding,
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${r.card.border}`,
  borderRadius: r.card.radius,
  textDecoration: "none",
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "2px",
  },
};

export const ticketCardHeadSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: r.card.headGap,
};

export const ticketCardNumberSx: SxProps<Theme> = {
  fontSize: r.card.numberFontSize,
  fontWeight: t.weight.strong,
  color: t.color.accent,
  lineHeight: "normal",
  flexShrink: 0,
};

export const ticketCardPrioritySx = (priority: string): SxProps<Theme> => ({
  flexShrink: 0,
  p: r.card.priorityPadding,
  borderRadius: t.radius.pill,
  fontSize: r.card.priorityFontSize,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  color: priorityColors[priority] ?? t.color.muted,
  bgcolor: r.priorityTint[priority] ?? t.color.pillBg,
});

export const ticketCardCategorySx: SxProps<Theme> = {
  ml: "auto",
  pl: r.card.headGap,
  fontSize: r.card.categoryFontSize,
  color: r.card.categoryColor,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const ticketCardSummarySx: SxProps<Theme> = {
  fontSize: r.card.summaryFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: r.card.summaryLineHeight,
  wordBreak: "break-word",
};

export const ticketCardFooterSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: r.card.footerGap,
  pt: r.card.footerPaddingTop,
  borderTop: `${t.border.hairline} solid ${r.card.footerBorder}`,
};

export const ticketCardAvatarSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: r.card.avatarSize,
  height: r.card.avatarSize,
  borderRadius: t.radius.pill,
  bgcolor: r.card.avatarBg,
  color: r.card.avatarColor,
  fontSize: r.card.avatarFontSize,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
};

export const ticketCardReporterSx: SxProps<Theme> = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: r.card.reporterFontSize,
  color: t.color.muted,
  lineHeight: "normal",
};

export const ticketCardDateSx: SxProps<Theme> = {
  ml: "auto",
  pl: r.card.footerGap,
  flexShrink: 0,
  fontSize: r.card.dateFontSize,
  color: r.card.categoryColor,
  lineHeight: "normal",
};

/** Cards are stacked as siblings, spaced like the rest of the settings body. */
export const ticketCardListSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};
